import * as types from '../types';
import { IImportExportApi } from '../contracts';
import { HttpApi } from './HttpApi';
import { withJsonErrorBody } from './attachments';

/**
 * The filename from a Content-Disposition header (filename*=UTF-8''... first,
 * then filename="..."), or the fallback. Same-origin deployments (/api)
 * expose the header; on another origin it is not in CORS exposedHeaders, so
 * the fallback is used.
 */
export function filenameFromDisposition(header: unknown, fallback: string): string {
  if (typeof header !== 'string') return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : fallback;
}

const today = () => new Date().toISOString().slice(0, 10);

export class ImportExportHttpApi extends HttpApi implements IImportExportApi {
  async listImportSpecs(): Promise<types.ImportEntitySpec[]> {
    const response = await this.http.get<types.APIResponse<types.ImportEntitySpec[]>>('/import-export/import-specs');
    return response.data.data;
  }

  async downloadImportTemplate(entity: types.ImportEntity): Promise<types.CsvDownload> {
    return this.downloadCsv(`/import-export/templates/${encodeURIComponent(entity)}`, undefined, `${entity}-import-template.csv`);
  }

  async validateImport(entity: types.ImportEntity, file: File, options: types.ImportUploadOptions = {}): Promise<types.ImportValidation> {
    const response = await this.http.post<types.APIResponse<types.ImportValidation>>(
      `/import-export/imports/${encodeURIComponent(entity)}/validate`,
      this.form(file, options),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  async commitImport(entity: types.ImportEntity, file: File, options: types.ImportUploadOptions = {}): Promise<types.ImportCommitResult> {
    const response = await this.http.post<types.APIResponse<types.ImportCommitResult>>(
      `/import-export/imports/${encodeURIComponent(entity)}/commit`,
      this.form(file, options),
      // Large imports are one transaction on the server; allow it time
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180_000 }
    );
    return response.data.data;
  }

  async downloadExport(dataset: types.ExportDataset, filters: types.ExportFilters = {}): Promise<types.CsvDownload> {
    const params: Record<string, string> = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    return this.downloadCsv(`/import-export/exports/${encodeURIComponent(dataset)}`, params, `${dataset}-${today()}.csv`);
  }

  private form(file: File, options: types.ImportUploadOptions): FormData {
    const form = new FormData();
    // Text fields first, so the server has them before the file part
    if (options.preset) form.append('preset', options.preset);
    if (options.defaultTaxRateId) form.append('defaultTaxRateId', options.defaultTaxRateId);
    if (options.expectedSha256) form.append('expectedSha256', options.expectedSha256);
    form.append('file', file, file.name);
    return form;
  }

  private async downloadCsv(url: string, params: Record<string, string> | undefined, fallback: string): Promise<types.CsvDownload> {
    try {
      const response = await this.http.get<Blob>(url, { responseType: 'blob', params, timeout: 300_000 });
      return { blob: response.data, filename: filenameFromDisposition(response.headers?.['content-disposition'], fallback) };
    } catch (error) {
      throw await withJsonErrorBody(error);
    }
  }
}
